import { useEffect, useRef, useState } from 'react';
import { Outlet, useParams, useLocation } from 'react-router-dom';
import {
  PermissionDenied,
  PermissionEnum,
  useQueryState,
  useGetList,
  SchemaFilterType,
  useFilters,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import Accounts from '../../components/bank-and-psp/accounts';
import {
  useLazyGetConfigurationPspAccountsQuery,
  useLazyGetBankCASPQuery,
} from './store/services';
import { PspAccountItem } from './types';
import { accountsSchema, filtersSchema } from './variables/accounts';
import {
  clearAccountsListState,
  setAccountsTakeCount,
} from './store/slices/info';
import { clearBankAndPspAccountPageState } from './store/slices/account';

import cn from './bank-and-psp.module.css';

const PSPAccounts = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [takeCount, setTakeCount] = useState(0);

  const {
    bankAndPsp: {
      info: { generalInfo, accounts, balances, accountsPagination, casp },
      accountPage: { generalInfo: accountGeneralInfo },
    },
    configurationDictionaries: {
      currencies,
      activePermissions,
      overdraftEnabled,
    },
  } = useAppSelector((state) => state);

  const { bankId } = useParams();

  const { search } = useLocation();

  const [
    getAccounts,
    { isLoading: accountsLoading, isFetching: accountsFetching },
  ] = useLazyGetConfigurationPspAccountsQuery();
  const [getBankCasp] = useLazyGetBankCASPQuery();

  const { filters, onSortChange, onFilterChange } =
    useQueryState(accountsSchema);

  const dispatch = useAppDispatch();

  const { getListAction, setPage, errorList, onFilter } =
    useGetList<PspAccountItem>({
      getList: getAccounts,
      onFilterChange,
      onSortChange,
      schema: accountsSchema,
      itemId: bankId,
    });

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  useEffect(() => {
    if (!casp) {
      getBankCasp();
    }
  }, [casp]);

  useEffect(() => {
    if (accountGeneralInfo) {
      dispatch(clearBankAndPspAccountPageState());
    }
  }, [accountGeneralInfo]);

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 58;

        setTakeCount(Math.ceil(take + 3));
        dispatch(setAccountsTakeCount(Math.ceil(take + 3)));
      }
    }
  }, [scrollRef]);

  useEffect(() => {
    if (
      bankId &&
      !accounts &&
      currencies &&
      activePermissions?.includes(PermissionEnum.PSP_LIST) &&
      takeCount !== 0 &&
      search &&
      Object.keys(filters).length
    ) {
      getListAction({
        filters: {
          ...filters,
          take: {
            value: [takeCount.toString()],
            type: SchemaFilterType.STRING,
          },
        },
      });
    }
  }, [
    bankId,
    accounts,
    currencies,
    activePermissions,
    takeCount,
    search,
    filters,
  ]);

  useEffect(() => {
    if (
      bankId &&
      !accounts &&
      currencies &&
      activePermissions?.includes(PermissionEnum.PSP_LIST) &&
      takeCount !== 0 &&
      !Object.keys(filters).length &&
      !search.includes('cursor')
    ) {
      getListAction({
        filters: {
          take: {
            value: [takeCount?.toString()],
            type: SchemaFilterType.STRING,
          },
        },
      });
    }
  }, [
    bankId,
    accounts,
    currencies,
    activePermissions,
    takeCount,
    filters,
    search,
  ]);

  useEffect(
    () => () => {
      dispatch(clearAccountsListState());
    },
    [],
  );

  let content;

  if (!activePermissions?.includes(PermissionEnum.PSP_LIST)) {
    content = <PermissionDenied />;
  } else {
    content = (
      <div className={cn.contentWrapper} ref={scrollRef}>
        <Accounts
          isLoading={accountsLoading}
          isFetching={accountsFetching}
          accounts={accounts}
          bankId={bankId}
          balances={balances}
          integrated={Boolean(generalInfo?.pspIntegrationId)}
          pspIntegration={generalInfo?.pspIntegration}
          accountsError={errorList}
          overdraftEnabled={overdraftEnabled}
          setPage={setPage}
          handleSubmit={handleSubmit}
          filters={filters}
          takeCount={takeCount}
          filtersComponent={filtersComponent}
          accountsPagination={accountsPagination}
          casp={casp?.map((item) => ({ name: item.name, value: item.id }))}
          onFilter={onFilter}
          search={search}
        />
        <Outlet />
      </div>
    );
  }

  return content;
};

export default PSPAccounts;
